/**
 * useGameLoop — orchestrates the core game loop.
 *
 * All persistent game state lives in the Zustand gameStore (localStorage-backed).
 * This hook only contains async orchestration: API calls, animation timing, and
 * the intro/round flow. It reads fresh state via `gs()` (= getState()) inside
 * async functions to avoid stale closures.
 *
 * Returns the same interface as before so GameScreen barely changes.
 */
import { useEffect, useRef } from 'react';
import { useGameStore } from '../stores/gameStore';
import type { Character, ChatMessage, GameCard, ActiveBuff } from '../types';
import { CardType } from '../types';
import {
  MAX_TURNS,
  CP_WIN_THRESHOLD,
  CP_LOSE_THRESHOLD,
  DRAFT_INTERVAL,
  EXCHANGES_PER_ROUND,
  MAX_HAND_SIZE,
} from '../constants';
import {
  generateChatResponse,
  generateCards,
  generateEnding,
} from '../services/api/gameApi';

// Re-export for backward compat
export type { GamePhase } from '../stores/gameStore';

// ── Unique ID generator (stable across re-renders) ─────────────────
let _idCounter = 0;
const nextId = (prefix: string) => `${prefix}-${Date.now()}-${++_idCounter}`;

/** Shorthand for fresh store snapshot inside async functions. */
const gs = () => useGameStore.getState();

/** Get active buffs for a given speaker from current state. */
const getBuffsForSpeaker = (speakerId: string): ActiveBuff[] => {
  const s = gs();
  if (speakerId === s.charA?.id) return s.activeBuffsA;
  if (speakerId === s.charB?.id) return s.activeBuffsB;
  return [];
};

export function useGameLoop() {
  const store = useGameStore();
  const chatEndRef = useRef<HTMLDivElement>(null);
  /** Synchronous mutex — prevents intro & handleNextTurn from running concurrently. */
  const busyRef = useRef(false);
  /** Holds pre-generated first speaker's response produced during PLAYER_TURN. */
  const preGenRef = useRef<{
    promise: Promise<{ texts: string[]; cpChange: number; landmineTriggered: string | null }>;
    valid: boolean;
  } | null>(null);

  // ── Auto-scroll ────────────────────────────────────────────────────
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [store.messages, store.isTyping]);

  // ── Helper: deliver multi-bubble response with typing animation ───
  const deliverBubbles = async (texts: string[], speakerId: string) => {
    for (let i = 0; i < texts.length; i++) {
      if (i > 0) {
        gs().setIsTyping(true);
        const delay = Math.min(400 + texts[i].length * 30, 1500);
        await new Promise((r) => setTimeout(r, delay));
      }
      // Hide typing + append message in the same synchronous block
      gs().setIsTyping(false);
      gs().appendMessage({
        id: nextId('msg'),
        senderId: speakerId,
        text: texts[i],
        type: 'text',
      });
      if (i < texts.length - 1) {
        await new Promise((r) => setTimeout(r, 200));
      }
    }
  };

  // ── Helper: build extended history with not-yet-displayed messages ──
  const buildExtendedHistory = (
    currentMessages: ChatMessage[],
    newTexts: string[],
    speakerId: string,
  ): ChatMessage[] => [
    ...currentMessages,
    ...newTexts.map((text) => ({
      id: nextId('pre'),
      senderId: speakerId,
      text,
      type: 'text' as const,
    })),
  ];

  // ── Helper: deliver pre-generated texts with typing animation ───────
  const deliverResponse = async (texts: string[], speakerId: string) => {
    const s = gs();
    s.setLoadingText('');
    s.setIsTyping(true);
    const speaker = s.charA?.id === speakerId ? s.charA : s.charB!;
    s.setTypingSpeaker(speaker);

    const typingDelay = Math.min(500 + (texts[0]?.length || 0) * 30, 2500);
    await new Promise((r) => setTimeout(r, typingDelay));

    await deliverBubbles(texts, speakerId);
    gs().setIsTyping(false);
    gs().setTypingSpeaker(null);
  };

  // ── Helper: apply CP change to the speaker's own affection side ─────
  const applyCpChange = (cpChange: number, speakerId: string, landmineTriggered?: string | null) => {
    const s = gs();
    if (speakerId === s.charA?.id) {
      const newCp = Math.min(100, Math.max(0, s.cpA + cpChange));
      s.setCpA(newCp);
    } else {
      const newCp = Math.min(100, Math.max(0, s.cpB + cpChange));
      s.setCpB(newCp);
    }

    // Show dramatic system message when a landmine is triggered
    if (landmineTriggered) {
      const speakerName = speakerId === s.charA?.id ? s.charA?.name : s.charB?.name;
      gs().appendMessage({
        id: nextId('mine'),
        senderId: 'system',
        text: `💣 踩雷！${speakerName} 的雷区被引爆！好感度 ${cpChange}`,
        type: 'system',
      });
    }
  };

  // ── Helper: get dual CP values oriented for a specific speaker ─────
  const getCpForSpeaker = (speakerId: string): { speakerCp: number; listenerCp: number } => {
    const s = gs();
    if (speakerId === s.charA?.id) {
      return { speakerCp: s.cpA, listenerCp: s.cpB };
    }
    return { speakerCp: s.cpB, listenerCp: s.cpA };
  };

  // ── Helper: execute one A→B exchange ──────────────────────────────
  const executeSingleExchange = async (
    sp1: Character,
    sp2: Character,
    currentMessages: ChatMessage[],
    instruction1?: string,
    instruction2?: string,
    preGeneratedFirstResponse?: { texts: string[]; cpChange: number; landmineTriggered?: string | null } | null,
  ) => {
    let texts1: string[];

    if (preGeneratedFirstResponse) {
      texts1 = preGeneratedFirstResponse.texts;
      applyCpChange(preGeneratedFirstResponse.cpChange, sp1.id, preGeneratedFirstResponse.landmineTriggered);
    } else {
      const cp1 = getCpForSpeaker(sp1.id);
      const res1 = await generateChatResponse(
        sp1.id, sp2.id, currentMessages, instruction1,
        cp1.speakerCp, cp1.listenerCp, gs().pastEvents, getBuffsForSpeaker(sp1.id),
      );
      texts1 = res1.texts;
      applyCpChange(res1.cpChange, sp1.id, res1.landmineTriggered);
    }

    await deliverResponse(texts1, sp1.id);
    await new Promise((r) => setTimeout(r, 300));

    const extHistory = buildExtendedHistory(currentMessages, texts1, sp1.id);
    const cp2 = getCpForSpeaker(sp2.id);
    const res2 = await generateChatResponse(
      sp2.id, sp1.id, extHistory, instruction2,
      cp2.speakerCp, cp2.listenerCp, gs().pastEvents, getBuffsForSpeaker(sp2.id),
    );
    applyCpChange(res2.cpChange, sp2.id, res2.landmineTriggered);
    await deliverResponse(res2.texts, sp2.id);
  };

  // ── Helper: execute a full round = EXCHANGES_PER_ROUND exchanges ──
  // Card instructions only apply to the first exchange; subsequent ones flow naturally.
  const executeRound = async (
    sp1: Character,
    sp2: Character,
    currentMessages: ChatMessage[],
    instruction1?: string,
    instruction2?: string,
    preGeneratedFirstResponse?: { texts: string[]; cpChange: number; landmineTriggered?: string | null } | null,
  ) => {
    for (let i = 0; i < EXCHANGES_PER_ROUND; i++) {
      const inst1 = i === 0 ? instruction1 : undefined;
      const inst2 = i === 0 ? instruction2 : undefined;
      const preGen = i === 0 ? preGeneratedFirstResponse : null;

      await executeSingleExchange(
        sp1, sp2, gs().messages, inst1, inst2, preGen,
      );
      // Brief pause between exchanges
      if (i < EXCHANGES_PER_ROUND - 1) {
        await new Promise((r) => setTimeout(r, 400));
      }
    }
  };

  // ── Helper: risk level → intensity description for LLM ──────────────
  const getRiskIntensityHint = (card: GameCard): string => {
    const riskHints: Record<string, string> = {
      safe: `【强度：温和】此干预影响较轻(CP ${card.cpRange})，对话波动应该较小，情绪反应平缓。`,
      normal: `【强度：中等】此干预影响不确定(CP ${card.cpRange})，可能好也可能坏，角色反应可大可小。`,
      risky: `【强度：剧烈】此干预影响极大(CP ${card.cpRange})，角色应表现出强烈情绪反应，对话可能急转直下或爆发火花。`,
    };
    return riskHints[card.riskLevel] || riskHints.normal;
  };

  // ── Helper: resolve card instruction ───────────────────────────────
  // Card effects are now derived entirely from type + description + cpRange.
  const resolveCardInstruction = (
    card: GameCard | undefined,
    target: 'A' | 'B' | undefined,
    currentSpeakerId: string,
  ): string | undefined => {
    if (!card || !target) return undefined;
    const s = gs();
    const isCharA = currentSpeakerId === s.charA!.id;
    const intensityHint = getRiskIntensityHint(card);

    // 事件卡：双方都受影响
    if (card.type === CardType.EVENT) {
      return [
        `【突发事件】${card.name}`,
        `发生了什么：${card.description}`,
        intensityHint,
        `你必须对这个突发事件做出反应，并将其融入接下来的对话中。`,
      ].join('\n');
    }

    // 话题卡/人设卡：只有 target 角色收到指令
    const isTarget =
      (target === 'A' && isCharA) || (target === 'B' && !isCharA);
    if (!isTarget) return undefined;

    // 话题卡：强制话题引导
    if (card.type === CardType.TOPIC) {
      return [
        `【话题引导 - 最高优先级】你必须在接下来的对话中将话题转向：「${card.name}」`,
        `具体方向：${card.description}`,
        intensityHint,
        `你需要找到一个切入点把话题转到这个方向。可以自然过渡，也可以生硬地切换——但你一定要聊到这个话题，不能忽略这个指令。`,
      ].join('\n');
    }

    // 人设卡：首轮注入指令（持久 BUFF 由 speakerBuffs 单独处理）
    if (card.type === CardType.PERSONA) {
      return [
        `【人设突变】你的人设刚刚发生了变化：「${card.name}」`,
        `变化内容：${card.description}`,
        intensityHint,
        `这个变化是立刻生效的，请在下一句话中就体现出来。`,
      ].join('\n');
    }

    return undefined;
  };

  // ── Helper: draw cards from deck to hand (fill up to MAX_HAND_SIZE) ──
  const drawCardsFromDeck = () => {
    const s = gs();
    const currentHand = s.cards;
    const deck = s.deck;
    const slotsAvailable = MAX_HAND_SIZE - currentHand.length;
    if (slotsAvailable <= 0) return currentHand;
    
    // Get cards from deck that are not already in hand
    const availableCards = deck.filter(
      (d) => !currentHand.some((c) => c.id === d.id)
    );
    
    const cardsToDraw = availableCards.slice(0, slotsAvailable);
    const newHand = [...currentHand, ...cardsToDraw];
    
    gs().setCards(newHand);
    return newHand;
  };

  // ── Helper: enter swap phase ───────────────────────────────────────
  const handleSwap = () => {
    gs().setPhase('SWAP_CARDS');
  };

  // ── Helper: process swap completion ──────────────────────────────
  const handleSwapComplete = (selectedCardIds: string[]) => {
    if (selectedCardIds.length === 0) {
      // Cancelled — go back to player turn
      gs().setPhase('PLAYER_TURN');
      return;
    }

    const s = gs();
    s.moveCardsToHand(selectedCardIds);
    s.incrementSwapCount();

    // Check if hand exceeds limit
    if (gs().cards.length > MAX_HAND_SIZE) {
      // Store pending info for the discard modal
      gs().setPhase('DISCARD_CARDS');
    } else {
      gs().setPhase('PLAYER_TURN');
    }
  };

  // ── Helper: cancel swap (return to PLAYER_TURN) ──────────────────
  const handleSwapCancel = () => {
    gs().setPhase('PLAYER_TURN');
  };

  // ── Helper: process discard completion (hand overflow after swap) ──
  const handleDiscardComplete = async (discardedCardIds: string[]) => {
    if (busyRef.current) return;
    busyRef.current = true;

    try {
      const s = gs();
      
      // Remove discarded cards from hand
      const newHand = s.cards.filter((c) => !discardedCardIds.includes(c.id));
      s.setCards(newHand);
      
      // Also remove from deck (permanently consumed)
      const newDeck = s.deck.filter((c) => !discardedCardIds.includes(c.id));
      s.setDeck(newDeck);
      
      s.setPendingCards([]);
      
      // Enter player turn
      s.setPhase('PLAYER_TURN');
      
      if (discardedCardIds.length > 0) {
        gs().appendMessage({
          id: nextId('sys'),
          senderId: 'system',
          text: `弃掉了 ${discardedCardIds.length} 张卡牌`,
          type: 'system',
        });
      }
    } finally {
      busyRef.current = false;
    }
  };

  // ── Init: fresh game → intro dialogue + cards; OR recovery ─────────────
  useEffect(() => {
    const s = gs();
    if (s.step !== 'GAME' || !s.charA || !s.charB) return;
    if (busyRef.current) return;

    // Fresh game → run intro dialogue + generate cards into deck
    if (s.messages.length === 0 && s.turn === 1 && s.deck.length === 0) {
      busyRef.current = true;

      (async () => {
        try {
          const charA = s.charA!;
          const charB = s.charB!;
          const sp1 = s.firstSpeaker === 'A' ? charA : charB;
          const sp2 = s.firstSpeaker === 'A' ? charB : charA;

          // Start card generation in background while intro plays
          const cardsPromise = generateCards(charA.id, charB.id, [], gs().cpA, gs().cpB);

          // Execute intro dialogue (self-introduction — single exchange)
          gs().setLoadingText('自我介绍中...');
          const introInstruction =
            '这是你们第一次见面。请做简短的自我介绍，包括名字和你觉得有趣的关于自己的事情。';
          await executeSingleExchange(sp1, sp2, [], introInstruction, introInstruction);

          // Intro round done — advance turn
          gs().setTurn(2);

          // Wait for cards then add all to deck directly (no draft popup)
          gs().setLoadingText('生成卡牌中...');
          const newCards = await cardsPromise;
          const deckCards = Array.isArray(newCards) ? newCards : [];
          gs().addCardsToDeck(deckCards);

          // Auto-draw to fill hand
          drawCardsFromDeck();
          gs().setPhase('PLAYER_TURN');
          gs().setLoadingText('');
        } finally {
          busyRef.current = false;
        }
      })();
      return;
    }

    // Recovery: intro completed but cards never loaded (e.g. page crash)
    if (s.messages.length > 0 && s.deck.length === 0 && s.cards.length === 0
      && s.pendingCards.length === 0) {
      busyRef.current = true;
      (async () => {
        try {
          gs().setLoadingText('生成卡牌中...');
          const newCards = await generateCards(
            s.charA!.id, s.charB!.id, gs().messages, gs().cpA, gs().cpB,
          );
          const deckCards = Array.isArray(newCards) ? newCards : [];
          gs().addCardsToDeck(deckCards);
          drawCardsFromDeck();
          gs().setPhase('PLAYER_TURN');
          gs().setLoadingText('');
        } finally {
          busyRef.current = false;
        }
      })();
      return;
    }

    // Interrupted game → recover
    if (s.phase !== 'PLAYER_TURN' && s.phase !== 'SWAP_CARDS' && s.phase !== 'DISCARD_CARDS') {
      if (s.pendingCards.length > 0) {
        gs().setPhase('DISCARD_CARDS');
      } else {
        // Refill hand from deck and resume
        if (s.cards.length < MAX_HAND_SIZE && s.deck.length > 0) {
          drawCardsFromDeck();
        }
        gs().setPhase('PLAYER_TURN');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Pre-generate first speaker's response during PLAYER_TURN ────────
  // While the player is deciding, pre-generate the first speaker's first message.
  // This gives us a head start when the player clicks "skip" or uses a card.
  useEffect(() => {
    if (store.phase !== 'PLAYER_TURN' || !store.charA || !store.charB) return;

    const s = gs();
    const sp1 = s.firstSpeaker === 'A' ? s.charA! : s.charB!;
    const sp2 = s.firstSpeaker === 'A' ? s.charB! : s.charA!;
    const currentMessages = [...s.messages];
    const currentPastEvents = s.pastEvents ? [...s.pastEvents] : [];

    // Pre-generate first speaker's first message only (with current buffs)
    const sp1Cp = getCpForSpeaker(sp1.id);
    const sp1Buffs = sp1.id === s.charA?.id ? [...s.activeBuffsA] : [...s.activeBuffsB];
    const preGen = generateChatResponse(
      sp1.id, sp2.id, currentMessages, undefined,
      sp1Cp.speakerCp, sp1Cp.listenerCp, currentPastEvents, sp1Buffs,
    );

    preGenRef.current = { promise: preGen, valid: true };

    return () => {
      if (preGenRef.current) preGenRef.current.valid = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.phase]);

  // ── Core round handler ─────────────────────────────────────────────
  const handleNextTurn = async (card?: GameCard, target?: 'A' | 'B') => {
    if (busyRef.current) return;
    busyRef.current = true;

    try {
      const s = gs();
      s.setPhase('PROCESSING');
      s.setSelectedCard(null);

      const canUsePreGen = !card && preGenRef.current?.valid;
      const preGenPromise = canUsePreGen ? preGenRef.current!.promise : null;
      preGenRef.current = null;

      const charA = s.charA!;
      const charB = s.charB!;

      // 决定本回合发言顺序：使用卡牌时，被使用的角色先开口；否则用默认顺序
      let sp1: Character;
      let sp2: Character;
      if (card && target) {
        // 被使用卡牌的角色先说话
        sp1 = target === 'A' ? charA : charB;
        sp2 = target === 'A' ? charB : charA;
      } else {
        sp1 = s.firstSpeaker === 'A' ? charA : charB;
        sp2 = s.firstSpeaker === 'A' ? charB : charA;
      }

      // 1. Player Action
      if (card && target) {
        gs().setLoadingText('使用卡牌...');

        if (card.type === CardType.EVENT) {
          gs().appendEvent(`${card.name}：${card.description}`);
        }

        // 人设卡：添加持久 BUFF 到目标角色
        if (card.type === CardType.PERSONA) {
          const buff: ActiveBuff = {
            id: card.id,
            cardName: card.name,
            description: card.description,
          };
          if (target === 'A') {
            gs().appendBuffA(buff);
          } else {
            gs().appendBuffB(buff);
          }
        }

        gs().appendMessage({
          id: nextId('sys'),
          senderId: 'system',
          text: `对 ${target === 'A' ? charA.name : charB.name} 使用了【${card.name}】：${card.description}`,
          type: 'system',
        });

        // Remove used card from hand AND deck (card is consumed permanently)
        gs().removeCardFromHand(card.id);
        gs().removeCardFromDeck(card.id);
        
        await new Promise((r) => setTimeout(r, 1500));
      } else {
        gs().appendMessage({
          id: nextId('sys'),
          senderId: 'system',
          text: '你选择了静观其变',
          type: 'system',
        });
      }

      // 2. Resolve card instructions (only apply to the first exchange)
      const instruction1 = resolveCardInstruction(card, target, sp1.id);
      const instruction2 = resolveCardInstruction(card, target, sp2.id);

      // Get pre-generated first response if available (only for skip turn scenario)
      let preGeneratedFirstResponse: { texts: string[]; cpChange: number; landmineTriggered: string | null } | null = null;
      if (canUsePreGen && preGenPromise) {
        try {
          preGeneratedFirstResponse = await preGenPromise;
        } catch {
          // Pre-gen failed, will generate on the fly
        }
      }

      // 3. Execute full round (EXCHANGES_PER_ROUND A→B exchanges)
      await executeRound(sp1, sp2, gs().messages, instruction1, instruction2, preGeneratedFirstResponse);

      // 4. Game-end check (dual CP was updated inline during dialogue)
      const { cpA: newCpA, cpB: newCpB } = gs();
      const nextTurn = gs().turn + 1;
      const bothWin = newCpA >= CP_WIN_THRESHOLD && newCpB >= CP_WIN_THRESHOLD;
      const eitherLose = newCpA <= CP_LOSE_THRESHOLD || newCpB <= CP_LOSE_THRESHOLD;
      if (nextTurn > MAX_TURNS || bothWin || eitherLose) {
        gs().setLoadingText('生成结局中...');
        const result = await generateEnding(
          charA.id, charB.id, gs().messages, newCpA, newCpB,
        );
        gs().endGame(result);
        return;
      }

      gs().setTurn(nextTurn);

      // 5. Auto-generate cards into deck every DRAFT_INTERVAL turns
      const shouldAutoGen = (nextTurn - 1) % DRAFT_INTERVAL === 0;

      if (shouldAutoGen) {
        gs().setLoadingText('补充卡牌中...');
        const newCards = await generateCards(
          charA.id, charB.id, gs().messages, newCpA, newCpB,
        );
        const deckCards = Array.isArray(newCards) ? newCards : [];
        gs().addCardsToDeck(deckCards);
        gs().setLoadingText('');
        gs().appendMessage({
          id: nextId('sys'),
          senderId: 'system',
          text: `牌堆补充了 ${deckCards.length} 张新卡牌！`,
          type: 'system',
        });
      }

      // 6. Refill hand from deck, then go to PLAYER_TURN
      drawCardsFromDeck();
      gs().setPhase('PLAYER_TURN');
    } finally {
      busyRef.current = false;
    }
  };

  // ── Public API ─────────────────────────────────────────────────────
  return {
    charA: store.charA!,
    charB: store.charB!,
    turn: store.turn,
    cpA: store.cpA,
    cpB: store.cpB,
    messages: store.messages,
    cards: store.cards,
    deck: store.deck,
    swapCount: store.swapCount,
    pendingCards: store.pendingCards,
    phase: store.phase,
    loadingText: store.loadingText,
    selectedCard: store.selectedCard,
    setSelectedCard: store.setSelectedCard,
    isTyping: store.isTyping,
    typingSpeaker: store.typingSpeaker,
    handleNextTurn,
    handleSwap,
    handleSwapComplete,
    handleSwapCancel,
    handleDiscardComplete,
    chatEndRef,
  };
}
